(ns ui.view.opensearch-value.format
  "The text of the value of a field that a listing shows. The grid and the cards
   of the Kanban board, the list and the gallery show the same value, so they use
   the same text: a number with the thousand separators and 2 decimals, a currency
   value with its currency and its multiplier, a percentage and a multiplier. The
   functions here do not read the session or the row of the grid. The caller gives
   the data that a text needs."
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.date-formats :as date-formats]
   [common.dates :as dates]
   [common.opensearch.totals :as totals]
   [common.timezones :as timezones]
   [frontend.utils :as fu]))

(defn number-text
  "A number with the thousand separators and 2 to 3 decimals. A missing number
   gives the empty text."
  [value]
  (if (some? value)
    (fu/format-with-separators value 2 false 3)
    ""))

(defn percentage-text
  "A fraction as a percentage: 0.1234 gives 12.34%. A missing value gives the empty
   text."
  [value]
  (if (some? value)
    (str (-> value
             (* 100)
             (fu/round 2)
             (fu/format-with-separators 2 false 2))
         "%")
    ""))

(defn multiplier-text
  "A multiplier with 3 decimals: 1.5 gives 1.500x. A missing value gives the empty
   text. The entity viewer shows the same text, from `fu/format-multiplier`."
  [value]
  (or (fu/format-multiplier value)
      ""))

(defn currency-label
  "The label of a currency, for example `EUR` for `:eur`. `currency` is the
   currency of the value. When it has no label, the label of `default-currency`
   is used. A currency that has no label is shown as it is. The result is the
   empty text when there is no currency at all."
  [{:keys [currency default-currency]}]
  (str (or (currencies/get-label currency)
           (currencies/get-label default-currency)
           currency
           "")))

(defn currency-parts
  "A currency value in two parts: the label of the currency and the number with the
   multiplier of the field, for example `{:label \"EUR\" :amount \"6,700,000.00\"}`.
   The label is the empty text when there is no currency label. A missing value
   gives nil. `number-multiplier` is the multiplier of the field, or nil for no
   multiplier. A view that shows the two parts apart uses this function."
  [{:keys [value currency default-currency number-multiplier]}]
  (when value
    {:label  (currency-label {:currency         currency
                              :default-currency default-currency})
     :amount (fu/format-number-with-mult (fu/bigdec->float value)
                                         (or number-multiplier ""))}))

(defn currency-text
  "A currency value: the label of the currency, a space and the number with the
   multiplier of the field, for example `EUR 6,700,000.00`. The text of a value
   with no currency label is the number only. A missing value gives nil.
   `number-multiplier` is the multiplier of the field, or nil for no multiplier."
  [args]
  (when-let [{:keys [label amount]} (currency-parts args)]
    (if (string/blank? label)
      amount
      (str label " " amount))))

(defn date-text
  "A date in the format that the tenant has set, for example `03.05.2006`.
   `date-format` is the pattern of the format. The index holds a date at midnight
   UTC, so it is shown in UTC, and the day does not depend on the time zone of the
   browser. A missing value, a missing format and a format that is not valid give
   nil."
  [{:keys [value date-format]}]
  (when (and (some? value)
             (string? date-format))
    (let [text (date-formats/format-millis value date-format timezones/default-timezone)]
      (when (string? text)
        text))))

(defn datetime-text
  "A date and a time in the format that the tenant has set, for example
   `19.04.2020 17:48`. The time has the 24 hours and the minutes, in the time zone
   of the browser, as the grid showed it before. A missing value, a missing format
   and a format that is not valid give nil."
  [{:keys [value date-format]}]
  (when (and (some? value)
             (string? date-format))
    (try
      (dates/format (js/Date. value) {:pattern (str date-format " HH:mm")})
      (catch :default _
        nil))))

(defn format-value
  "The text of `value` of a field of the type `data-type`, or nil when this
   namespace has no text for the type: the caller shows the value as it is, or
   formats it in another way. `currency`, `default-currency` and
   `number-multiplier` are for a currency value only."
  [{:keys [data-type value currency default-currency number-multiplier]}]
  (case data-type
    :data.type/number
    (number-text value)

    :data.type/currency-value
    (currency-text {:value             value
                    :currency          currency
                    :default-currency  default-currency
                    :number-multiplier number-multiplier})

    :data.type/percentage
    (percentage-text value)

    :data.type/multiplier
    (multiplier-text value)

    nil))

(defn- total-value-text
  [{:keys [value] :as args}]
  (or (format-value args)
      (str value)))

(defn total-text
  "The text of a total of a number field (see `common.opensearch.totals`). A total
   of a currency field has a part for each currency, in the order of the
   currencies, for example `EUR 12.4M · USD 3.1M`. Nil when there is no value."
  [{:keys [data-type total default-currency number-multiplier]}]
  (let [{:keys [value by-currency]} total
        text-of (fn [value currency]
                  (total-value-text {:data-type         data-type
                                     :value             value
                                     :currency          currency
                                     :default-currency  default-currency
                                     :number-multiplier number-multiplier}))]
    (cond
      (some? by-currency)
      (let [parts (->> (totals/with-default-currency by-currency default-currency)
                       (sort-by (comp str key))
                       (map (fn [[currency value]]
                              (text-of value currency))))]
        (when (seq parts)
          (string/join " · " parts)))

      (some? value)
      (text-of value nil))))
